<template>
  <div :class="$style.base">
    <div :class="$style.flex">
      <BaseText bold>{{ $t('marketing.email.sections.main_button') }}</BaseText>
      <BaseToggle
        v-if="!hideButtonToggle"
        v-model="hasButton"
        v-test="'toggleButton'"
      />
    </div>
    <Flex
      direction="column"
      v-show="hasButton"
      v-test="'main-button-actions'"
    >
      <BaseInput
        v-model="buttonTitle"
        :required="hasButton"
        :label="$t('marketing.email.sections.button_title')"
        v-test="'buttonMessage'"
      />
      <BaseRadio
        v-if="!hideButtonOptions"
        v-model="linkOption"
        :label="$t('marketing.email.sections.button_link')"
        :options="linkOptions"
        @update:modelValue="onLinkOptionSelect"
        v-test="'linkOptions'"
      />
      <BaseInput
        v-if="!hideButtonOptions"
        v-show="linkOption === 'other'"
        v-model="buttonUrl"
        :required="linkOption === 'other'"
        :label="$t('global.link')"
        v-test="'buttonLink'"
      />
    </Flex>
  </div>
</template>

<script lang="ts">
import useVuelidate from '@vuelidate/core';
import { mapState, storeToRefs } from 'pinia';
import { defineComponent } from 'vue';
import { useCompanyStore } from '@/stores/company';
import { useMicroSiteStore } from '@/stores/micro-site';
import { useMarketingStore } from '../../store';
import { useMarketingBuilderStore } from '../../store/builder';

export default defineComponent({
  setup() {
    const {
      isSubmitted,
      id,
      buttonTitle,
      buttonUrl,
      hasButton,
      buttonIsInvalid,
    } = storeToRefs(useMarketingStore());
    const builderStore = useMarketingBuilderStore();
    const { hideButtonToggle, hideButtonOptions } = storeToRefs(builderStore);
    return {
      v$: useVuelidate(),
      hideButtonToggle,
      hideButtonOptions,
      buttonIsInvalid,
      isSubmitted,
      id,
      buttonTitle,
      buttonUrl,
      hasButton,
    };
  },
  data() {
    return {
      linkOption: 'booking_widget',
      defaultLink: '',
    };
  },
  watch: {
    'v$.$invalid': {
      handler() {
        this.buttonIsInvalid = this.v$.$invalid;
      },
    },
    buttonUrl: {
      handler(value) {
        this.updateLinkOption(value);
      },
      immediate: true,
    },
    isSubmitted() {
      this.v$.$touch();
    },
    bookingWidgetUrl: {
      handler(value) {
        if (!value) return;

        this.defaultLink = value;
        if (!this.id) {
          this.buttonUrl = this.defaultLink;
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState(useCompanyStore, ['company']),
    ...mapState(useMicroSiteStore, ['bookingWidgetUrl']),
    linkOptions() {
      return [
        {
          label: this.$t('global.booking_widget'),
          value: 'booking_widget',
        },
        {
          label: this.$t('global.another_link'),
          value: 'other',
        },
      ];
    },
  },
  methods: {
    onLinkOptionSelect(value) {
      this.linkOption = value;
      const link =
        this.linkOption === 'booking_widget' ? this.bookingWidgetUrl : '';
      this.buttonUrl = link;
    },
    updateLinkOption(value: string | null) {
      const usingDefaultLink =
        value === null || value.endsWith('/widget_bookings/new');
      this.linkOption = !usingDefaultLink ? 'other' : 'booking_widget';
    },
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  border-top: 1px solid var(--color-border);
  padding: $spacing 0;
}

.flex {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
